import React, { FunctionComponent } from 'react';
import {
  StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import { moderateScale } from 'react-native-size-matters';
import Theme from '../../../theme';
import { defaultScale, fullScale, scaleText } from '../../../utils/scaling';
import { MainActionButton } from './MainActionButton';

interface IProps {
  userHasStartedWorkout: boolean;
  preloadingComplete: boolean;
  homeWorkoutVideoError: string | null;
  homeWorkoutLoading: boolean;
  isGroupWorkout: boolean;
  homeWorkoutLoadingError: string | null;
  landscape: boolean;
  waitingToStartWorkout: boolean;
  remainingSecondsInSequence: number | null;
  isPastWarmdown: boolean;
  shouldShowEliteInstructions: boolean;

  leaveWorkout(): void;

  closeDeviceSetup(): void;

  t(key: string, options?: any): string;

  leaveWorkoutClicked(): void;

  resumeWorkoutPressed(): void;
}

export const DeviceSetupContent: FunctionComponent<IProps> = ({
  userHasStartedWorkout,
  preloadingComplete,
  homeWorkoutVideoError,
  isGroupWorkout,
  leaveWorkout,
  leaveWorkoutClicked,
  t,
  homeWorkoutLoadingError,
  resumeWorkoutPressed,
  waitingToStartWorkout,
  remainingSecondsInSequence,
  landscape = false,
  isPastWarmdown,
  shouldShowEliteInstructions,
}) => {
  const closeButton = () => {
    if (isGroupWorkout && waitingToStartWorkout) {
      return (
        <MainActionButton
          label={t('leaveWorkout')}
          secondaryAction={leaveWorkout}
        />
      );
    }
    if (userHasStartedWorkout) {
      return (
        <MainActionButton
          label={t('endWorkout')}
          secondaryAction={leaveWorkoutClicked}
        />
      );
    }
    return (
      <MainActionButton
        label={t('leaveWorkout')}
        secondaryAction={leaveWorkout}
      />
    );
  };

  const playButtonLabel = () => {
    if (isGroupWorkout) {
      if (!waitingToStartWorkout) {
        return t('backToClass');
      }
      return t('joinClass');
    }
    if (isPastWarmdown) {
      return t('backToWorkout');
    }
    if (userHasStartedWorkout) {
      return t('resumeWorkout');
    }
    return t('startWorkoutDeviceSetup');
  };

  const infoSection = () => {
    if (isGroupWorkout) {
      if (waitingToStartWorkout && !landscape) {
        return (
          <Text style={[styles.text]}>
            {t('classStartsIn', { remainingSecondsInSequence })}
          </Text>
        );
      }
      return (
        <Text
          style={[styles.text]}
        >
          {t('Class in progress')}
        </Text>
      );
    }
    if (userHasStartedWorkout && !isGroupWorkout) {
      return <Text style={styles.text}>{t('workoutPaused')}</Text>;
    }
  };

  const setUpContent = () => {
    const textContainerStyles = [styles.textContainer,
      !shouldShowEliteInstructions && { flex: 1 }];
    if (homeWorkoutLoadingError) {
      return (
        <>
          <View style={textContainerStyles}>
            <Text style={[styles.text]}>{t('errorLine1')}</Text>
            <Text style={[styles.text]}>
              {t('errorLine2')}
            </Text>
          </View>
          <View style={styles.buttonsContainer}>
            <MainActionButton
              label={t('leaveWorkout')}
              secondaryAction={leaveWorkout}
            />
          </View>
        </>
      );
    }

    if (isPastWarmdown) {
      return (
        <>
          <TouchableOpacity onPress={resumeWorkoutPressed} style={textContainerStyles}>
            <Text style={[styles.text]}>
              {t('workoutCompleted')}
            </Text>
          </TouchableOpacity>
          <View style={styles.buttonsContainer}>
            {closeButton()}
            <MainActionButton
              label={playButtonLabel()}
              primaryAction={resumeWorkoutPressed}
            />
          </View>
        </>
      );
    }

    if (!preloadingComplete) {
      return (
        <>
          <View style={textContainerStyles}>
            <Text style={[styles.text]}>
              {t(homeWorkoutVideoError ? 'bufferingNextAssets' : 'settingUpWorkout')}
            </Text>
          </View>
          <View style={styles.buttonsContainer}>
            {closeButton()}
          </View>
        </>
      );
    }

    return (
      <>
        <TouchableOpacity onPress={resumeWorkoutPressed} style={textContainerStyles}>
          {infoSection()}
        </TouchableOpacity>
        <View style={styles.buttonsContainer}>
          {closeButton()}
          <MainActionButton
            label={playButtonLabel()}
            primaryAction={resumeWorkoutPressed}
          />
        </View>
      </>
    );
  };

  return (
    <View style={[styles.container, !shouldShowEliteInstructions && { flex: 1 }]}>
      {setUpContent()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderTopColor: Theme.GREY_DARK_MEDIUM,
    borderBottomColor: Theme.GREY_DARK_MEDIUM,
    justifyContent: 'center',
    marginHorizontal: defaultScale(10),
  },
  text: {
    ...Theme.textStyle.bodyCopyBold,
    fontSize: scaleText(20),
    color: Theme.WHITE,
    textAlign: 'center',
    justifyContent: 'center',
    paddingTop: defaultScale(10),
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  textContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});
